import React, { useState, useRef, useEffect } from "react";
import Transition from "../utils/Transition";

function DropdownMulti({
  options = [],
  selectedValues,
  setSelectedValues,
  displayKeys = false,
  emptyPlaceholder = "Select options",
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;

      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  const toggleOption = (optionId) => {
    const hasAll = Boolean(options.find((option) => option.id === "_all"));
    if (optionId === "_all") {
      if (selectedValues.includes("_all")) {
        setSelectedValues([]);
      } else {
        setSelectedValues(options.map((option) => option.id));
      }
    } else {
      if (selectedValues.includes(optionId)) {
        let newSet = selectedValues.filter((id) => id !== optionId);
        if (hasAll) {
          newSet = newSet.filter((id) => id !== "_all");
        }
        setSelectedValues(newSet);
      } else {
        let newSet = [...selectedValues, optionId];
        if (hasAll) {
          if (newSet.length === options.length - 1) {
            newSet = ["_all", ...newSet];
          }
        }
        setSelectedValues(newSet);
      }
    }
  };

  const orderedOptions = [
    ...options.filter((option) => selectedValues.includes(option.id)),
    ...options.filter((option) => !selectedValues.includes(option.id)),
  ];

  const selectedLabels = selectedValues
    .map(
      (id) =>
        options.find((option) => option.id === id)?.[
          displayKeys ? "id" : "label"
        ]
    )
    .filter(Boolean);

  const triggerLabel = (() => {
    if (selectedValues.length === 0) return emptyPlaceholder;
    if (selectedValues.includes("_all")) {
      return options.find((option) => option.id === "_all")?.label || emptyPlaceholder;
    }
    if (selectedLabels.length <= 2) {
      return selectedLabels.join(", ");
    }
    return `${selectedLabels[0]}, ${selectedLabels[1]}, and ${selectedLabels.length - 2} more`;
  })();

  return (
    <div className="relative inline-flex w-full">
      <button
        ref={trigger}
        className="btn bg-white border-gray-300 hover:border-gray-400 text-gray-800 w-full flex justify-between items-center truncate"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
        type={"button"}
      >
        <span className="truncate">{triggerLabel}</span>
        <svg
          className="w-4 h-4 ml-2 fill-current text-gray-500 flex-shrink-0"
          viewBox="0 0 20 20"
        >
          <path d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z" />
        </svg>
      </button>

      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-[11] absolute top-full left-0 w-full bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="font-medium text-sm text-slate-600 dark:text-slate-300"
        >
          {orderedOptions.map((option) => (
            <li
              key={option.id}
              className="py-1 px-3 cursor-pointer hover:bg-gray-100 flex items-center"
              onClick={() => toggleOption(option.id)}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option.id)}
                onChange={() => toggleOption(option.id)}
                className="mr-2"
              />
              {option.label}
            </li>
          ))}
        </div>
      </Transition>
    </div>
  );
}

export default DropdownMulti;
